import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const PROFILE = [["official_name", "Official company name"], ["address", "Address"], ["city", "City"], ["country", "Country"], ["phone", "Phone"], ["email", "Email"]];
const COMPANY = [["name", "Company name"], ["address", "Address"], ["city", "City"], ["country", "Country"], ["phone", "Phone"], ["email", "Email"]];

const pick = (o, fields) => Object.fromEntries(fields.map(([k]) => [k, o[k] || ""]));

function Missing({ items, testid }) {
  if (!items?.length) return <p className="mt-2 flex items-center gap-1 text-xs text-status-available" data-testid={testid}><CheckCircle2 className="h-3.5 w-3.5" /> Complete for supplier briefs</p>;
  return <p className="mt-2 flex items-start gap-1 text-xs font-medium text-status-reserved" data-testid={testid}><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Needs completion: {items.join(", ")}</p>;
}

function Fields({ fields, value, onChange, prefix }) {
  return (
    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
      {fields.map(([k, label]) => (
        <div key={k} className={k === "address" || k === "official_name" || k === "name" ? "sm:col-span-3" : ""}>
          <Label className="text-xs text-warmgrey">{label}</Label>
          <Input value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value })} data-testid={`${prefix}-${k}`}
            className={`mt-1 h-10 bg-white ${value[k] ? "" : "border-status-reserved/60"}`} />
        </div>
      ))}
    </div>
  );
}

function CompanyProfile() {
  const [p, setP] = useState(null);
  const [f, setF] = useState(null);
  const load = () => api.get("/admin/company-profile").then(({ data }) => { setP(data); setF(pick(data, COMPANY)); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!f) return null;
  const save = async () => {
    try { const { data } = await api.put("/admin/company-profile", f); setP(data); setF(pick(data, COMPANY)); toast.success("Zelligio profile saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="mt-8 rounded-md border border-border bg-white p-5" data-testid="company-profile-card">
      <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Zelligio details on supplier briefs</p>
      <Missing items={p.missing_fields} testid="company-profile-missing" />
      <Fields fields={COMPANY} value={f} onChange={setF} prefix="company" />
      <Button size="sm" onClick={save} className="mt-3 bg-terracotta text-white hover:bg-terracotta-hover" data-testid="company-profile-save">Save Zelligio details</Button>
    </div>
  );
}

function SupplierRow({ s, patch }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: s.name, code: s.code, ...pick(s, PROFILE) });
  const save = async () => { if (await patch(s, f)) { toast.success("Supplier saved"); setOpen(false); } };
  return (
    <div className="p-4" data-testid={`supplier-row-${s.code}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded bg-alabaster px-2 py-1 font-mono text-xs text-charcoal">{s.code}</span>
        <span className="text-sm font-medium text-charcoal" data-testid={`supplier-name-${s.code}`}>{s.name}</span>
        <span className="text-xs text-warmgrey">{[s.city, s.country].filter(Boolean).join(", ")}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline" className="h-8 text-xs"><Link to={`/admin/suppliers/${s.id}`} data-testid={`supplier-open-${s.code}`}>Settlement & details</Link></Button>
          <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid={`supplier-edit-${s.code}`} className="h-8 text-xs">{open ? "Close" : "Edit details"}</Button>
          <Button size="sm" variant="outline" onClick={() => patch(s, { active: !s.active })} data-testid={`supplier-toggle-${s.code}`} className="h-8 border-border text-xs">
            {s.active ? "Active — deactivate" : "Inactive — reactivate"}
          </Button>
        </div>
      </div>
      <Missing items={s.missing_fields} testid={`supplier-missing-${s.code}`} />
      {open && (
        <div className="mt-2 rounded-md bg-sand/40 p-4" data-testid={`supplier-form-${s.code}`}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2"><Label className="text-xs text-warmgrey">Short name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`supplier-${s.code}-name`} className="mt-1 h-10 bg-white" /></div>
            <div><Label className="text-xs text-warmgrey">Acronym / code (used in brief filenames)</Label><Input value={f.code} maxLength={6} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid={`supplier-${s.code}-code`} className="mt-1 h-10 bg-white font-mono" /></div>
          </div>
          <Fields fields={PROFILE} value={f} onChange={setF} prefix={`supplier-${s.code}`} />
          <p className="mt-2 text-[11px] text-muted-foreground">Logo: not used for now — briefs show the company name.</p>
          <Button size="sm" onClick={save} className="mt-3 bg-terracotta text-white hover:bg-terracotta-hover" data-testid={`supplier-save-${s.code}`}>Save supplier</Button>
        </div>
      )}
    </div>
  );
}

export default function AdminSuppliers() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", code: "" });
  const load = () => api.get("/admin/suppliers").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/suppliers", { ...form, country: "Morocco" });
      toast.success("Supplier created — complete its details");
      setForm({ name: "", code: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const patch = async (s, body) => {
    try {
      await api.patch(`/admin/suppliers/${s.id}`, body);
      load();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return false;
    }
  };

  return (
    <div data-testid="admin-suppliers-page">
      <p className="font-mono text-xs uppercase tracking-widest text-terracotta">Internal · Confidential</p>
      <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Supplier Directory</h1>
      <p className="mt-2 max-w-2xl text-sm text-warmgrey">Moroccan manufacturers supplying Zelligio. Custom Studio supplier briefs are addressed only to suppliers listed here. Never shown to dealers or ZELI dealer sessions.</p>
      <form onSubmit={create} className="mt-8 grid grid-cols-1 gap-3 rounded-md border border-border bg-white p-4 sm:grid-cols-3" data-testid="supplier-create-form">
        <Input required placeholder="Supplier short name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" className="h-11 bg-white" />
        <Input required placeholder="Acronym / code (e.g. MZ)" maxLength={6} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="supplier-code-input" className="h-11 bg-white font-mono" />
        <Button type="submit" data-testid="supplier-create-submit" className="h-11 bg-terracotta text-white hover:bg-terracotta-hover">Add supplier</Button>
      </form>
      <div className="mt-6 divide-y divide-border rounded-md border border-border bg-white">
        {rows.length === 0 && <p className="p-6 text-sm text-muted-foreground" data-testid="suppliers-empty">No suppliers yet.</p>}
        {rows.map((s) => <SupplierRow key={`${s.id}-${s.updated_at}`} s={s} patch={patch} />)}
      </div>
      <CompanyProfile />
    </div>
  );
}
